<template>
  <div class="sales-invoice-report-container">
    <!-- Enhanced Header with Gradient Background -->
    <div class="sales-invoice-report-header">
      <div class="sales-invoice-header-content">
        <div class="sales-invoice-header-text">
          <h2 class="sales-invoice-report-title">Sales Invoice Report</h2>
          <p class="sales-invoice-report-subtitle">Comprehensive sales invoice analysis and insights</p>
        </div>
       
      </div>
    </div>

    <!-- Enhanced Filters Section -->
    <div class="sales-invoice-filters-section">
      <b-card class="sales-invoice-enhanced-filter-card">
        <div class="sales-invoice-filter-header">
          <h5 class="sales-invoice-filter-title">
            <feather-icon icon="FilterIcon" size="20" class="mr-2" />
            Report Filters
          </h5>
        </div>
        
        <div class="filter-content">
          <b-row>
            <!-- Customer Filter -->
            <b-col md="4">
              <b-form-group label="Select Customer" label-for="customerFilter" class="enhanced-form-group">
                <v-select
                  id="customerFilter"
                  v-model="filters.selectedCustomer"
                  :options="customerOptions"
                  :clearable="true"
                  placeholder="All Customers"
                  class="enhanced-select"
                  @input="applyFilters"
                />
              </b-form-group>
            </b-col>

            <!-- Sales Type Filter -->
            <b-col md="4">
              <b-form-group label="Sales Type" label-for="salesTypeFilter" class="enhanced-form-group">
                <v-select
                  id="salesTypeFilter"
                  v-model="filters.salesType"
                  :options="salesTypeOptions"
                  :clearable="true"
                  placeholder="Select Sales Type"
                  class="enhanced-select"
                  @input="applyFilters"
                />
              </b-form-group>
            </b-col>

            <!-- Status Filter -->
            <b-col md="4">
              <b-form-group label="Status" label-for="statusFilter" class="enhanced-form-group">
                <v-select
                  id="statusFilter"
                  v-model="filters.status"
                  :options="statusOptions"
                  :clearable="true"
                  placeholder="Select Status"
                  class="enhanced-select"
                  @input="applyFilters"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <!-- From Date Filter -->
            <b-col md="4">
              <b-form-group label="From Date" label-for="fromDate" class="enhanced-form-group">
                <b-form-input
                  id="fromDate"
                  v-model="filters.fromDate"
                  type="date"
                  class="enhanced-input"
                  @change="applyFilters"
                />
              </b-form-group>
            </b-col>

            <!-- To Date Filter -->
            <b-col md="4">
              <b-form-group label="To Date" label-for="toDate" class="enhanced-form-group">
                <b-form-input
                  id="toDate"
                  v-model="filters.toDate"
                  type="date"
                  class="enhanced-input"
                  @change="applyFilters"
                />
              </b-form-group>
            </b-col>

            <!-- Invoice Number Filter -->
            <b-col md="4">
              <b-form-group label="Invoice Number" label-for="invoiceNumberFilter" class="enhanced-form-group">
                <b-form-input
                  id="invoiceNumberFilter"
                  v-model="filters.invoiceNumber"
                  placeholder="Search by invoice number..."
                  class="enhanced-input"
                  @input="applyFilters"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Filter Actions -->
          <div class="filter-actions">
            <b-button variant="outline-secondary" @click="resetFilters" class="action-btn reset-btn">
              <feather-icon icon="RefreshCwIcon" size="16" class="mr-1" />
              Reset Filters
            </b-button>
            <b-button variant="primary" @click="generateReport" class="action-btn generate-btn">
              <feather-icon icon="FileTextIcon" size="16" class="mr-1" />
              Generate Report
            </b-button>
          </div>
        </div>
      </b-card>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="sales-invoice-loading-container">
      <div class="loading-content">
        <b-spinner type="grow" variant="primary" class="mr-3"></b-spinner>
        <span class="loading-text">Loading sales invoice report...</span>
      </div>
    </div>
  </div>
</template>

<script>
import TableSkeleton from '@/components/common/skeletons/TableSkeleton.vue';
import { APIService } from '@/utils/api';
import { RESOURCES, DATATABLE_SERIAL_COLUMNS } from '@/utils/constants';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import vSelect from 'vue-select';
import {
  BCard,
  BButton,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BSpinner,
} from 'bootstrap-vue';

export default {
  name: 'SalesInvoiceReport',
  components: {
    TableSkeleton,
    ToastificationContent,
    vSelect,
    BCard,
    BButton,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BSpinner,
  },
  data() {
    return {
      RESOURCES,
      DATATABLE_SERIAL_COLUMNS,
      isLoading: true,
      isExporting: false,
      pageOptions: [
        { value: 20, text: '20' },
        { value: 40, text: '40' },
        { value: 60, text: '60' },
        { value: 100, text: '100' },
      ],
      pagination: {
        currentPage: 1,
        totalRows: 0,
      },
      filters: {
        currentPage: 1,
        perPage: 20,
        selectedCustomer: null,
        salesType: null,
        status: null,
        fromDate: '',
        toDate: '',
        invoiceNumber: '',
      },
      tableColumns: [
        ...DATATABLE_SERIAL_COLUMNS,
        { key: 'docNo', label: 'DOC NO', thStyle: { minWidth: '150px' } },
        { key: 'customerName', label: 'CUSTOMER NAME', thStyle: { minWidth: '200px' } },
        { key: 'tranDate', label: 'TRANSACTION DATE', thStyle: { minWidth: '150px' } },
        { key: 'salesTypeName', label: 'SALES TYPE', thStyle: { minWidth: '150px' } },
        { key: 'grossAmount', label: 'GROSS AMOUNT', thStyle: { minWidth: '150px' } },
        { key: 'totalTax', label: 'TOTAL TAX', thStyle: { minWidth: '150px' } },
        { key: 'netAmount', label: 'NET AMOUNT', thStyle: { minWidth: '150px' } },
        { key: 'status', label: 'STATUS', thStyle: { minWidth: '120px' } },
      ],
      salesInvoices: [],
      totalInvoices: 0,
      totalAmount: 0,
      paidInvoices: 0,
      pendingInvoices: 0,
      // Filter Options
      customerOptions: [],
      salesTypeOptions: [],
      statusOptions: [
        { label: 'Draft', value: 'Draft' },
        { label: 'Pending', value: 'Pending' },
        { label: 'Paid', value: 'Paid' },
        { label: 'Cancelled', value: 'Cancelled' },
      ],
    };
  },
  computed: {
    businessID() {
      if (this.$store.state.user && this.$store.state.user.businessID) {
        return this.$store.state.user.businessID;
      }
      if (this.$store.state.businessID) {
        return this.$store.state.businessID;
      }
      if (this.$store.state.userData && this.$store.state.userData.businessID) {
        return this.$store.state.userData.businessID;
      }
      return 1;
    },
    userID() {
      if (this.$store.state.user && this.$store.state.user.userID) {
        return this.$store.state.user.userID;
      }
      if (this.$store.state.userID) {
        return this.$store.state.userID;
      }
      if (this.$store.state.userData && this.$store.state.userData.userID) {
        return this.$store.state.userData.userID;
      }
      return 1;
    },
    paginationInfo() {
      const totalRows = this.pagination.totalRows;
      const start = totalRows === 0 ? 0 : (this.filters.currentPage - 1) * this.filters.perPage + 1;
      const end = Math.min(start + this.filters.perPage - 1, totalRows);
      return `${start} to ${end} of ${totalRows} entries`;
    },
  },
  watch: {
    filters: {
      handler(newVal) {
        if (newVal.currentPage !== undefined && newVal.perPage !== undefined) {
          this.loadSalesInvoices();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.loadSalesInvoices();
    this.loadFilterOptions();
  },
  methods: {
    applyFilters() {
      this.filters.currentPage = 1;
      this.loadSalesInvoices();
    },
    generateReport() {
      this.filters.currentPage = 1;
      this.loadSalesInvoices();
    },
    resetFilters() {
      this.filters = {
        currentPage: 1,
        perPage: 20,
        selectedCustomer: null,
        salesType: null,
        status: null,
        fromDate: '',
        toDate: '',
        invoiceNumber: '',
      };
      this.loadSalesInvoices();
    },
    handlePageChange(page) {
      this.filters.currentPage = page;
    },
    handlePerPageChange(perPage) {
      this.filters.perPage = perPage;
      this.filters.currentPage = 1;
    },
    async loadFilterOptions() {
      try {
        // Load customers from FBR API
        const customerResponse = await APIService.getByPagination(
          RESOURCES.CUSTOMERS_FBR,
          this.businessID,
          { pageNumber: 1, pageSize: 1000 }
        );
        if (customerResponse?.data?.data?.items) {
          this.customerOptions = customerResponse.data.data.items.map(customer => ({
            label: customer.name,
            value: customer.id
          }));
        }

        // Load sales types from FBR API
        const salesTypeResponse = await APIService.getByPagination(
          RESOURCES.SALES_TYPES,
          this.businessID,
          { pageNumber: 1, pageSize: 100 }
        );
        if (salesTypeResponse?.data?.data?.items) {
          this.salesTypeOptions = salesTypeResponse.data.data.items.map(type => ({
            label: type.name,
            value: type.id
          }));
        }
      } catch (error) {
        console.error('Error loading filter options:', error);
      }
    },
    async loadSalesInvoices() {
      this.isLoading = true;
      try {
        const params = {
          pageNumber: this.filters.currentPage,
          pageSize: this.filters.perPage,
          sortField: 'id',
          sortOrder: 'desc',
        };

        // Add filter parameters
        if (this.filters.selectedCustomer) params.customerID = this.filters.selectedCustomer;
        if (this.filters.salesType) params.salesTypeID = this.filters.salesType;
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.fromDate) params.fromDate = this.filters.fromDate;
        if (this.filters.toDate) params.toDate = this.filters.toDate;
        if (this.filters.invoiceNumber) params.docNo = this.filters.invoiceNumber;

        const response = await APIService.getByPagination(
          RESOURCES.SALES_INVOICE,
          this.businessID,
          params
        );

        if (response && response.data) {
          const apiPayload = response.data;
          this.pagination.totalRows = (apiPayload.data && apiPayload.data.totalCount) || 0;
          const results = (apiPayload.data && apiPayload.data.results) || [];
          
          this.salesInvoices = results.map((invoice, index) => ({
            ...invoice,
            serial: (this.filters.currentPage - 1) * this.filters.perPage + index + 1,
          }));

          // Calculate summary statistics
          this.calculateSummaryStats();
        }
      } catch (error) {
        console.error('Error loading sales invoices:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Failed to load sales invoice report',
          },
        });
      } finally {
        this.isLoading = false;
      }
    },
    calculateSummaryStats() {
      this.totalInvoices = this.salesInvoices.length;
      this.totalAmount = this.salesInvoices.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);
      this.paidInvoices = this.salesInvoices.filter(invoice => invoice.status === 'Paid').length;
      this.pendingInvoices = this.salesInvoices.filter(invoice => invoice.status === 'Pending').length;
    },
    getStatusVariant(status) {
      switch (status?.toLowerCase()) {
        case 'paid':
          return 'success';
        case 'pending':
          return 'warning';
        case 'cancelled':
          return 'danger';
        default:
          return 'secondary';
      }
    },
    formatCurrency(amount) {
      if (!amount) return 'N/A';
      return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
      }).format(amount);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString();
      } catch (error) {
        return 'Invalid Date';
      }
    },
    async exportData() {
      try {
        const params = {
          customerID: this.filters.selectedCustomer,
          salesTypeID: this.filters.salesType,
          status: this.filters.status,
          fromDate: this.filters.fromDate,
          toDate: this.filters.toDate,
          docNo: this.filters.invoiceNumber,
        };
        
        const response = await APIService.export(
          RESOURCES.SALES_INVOICE,
          this.businessID,
          this.userID,
          params
        );
        
        // Handle file download
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sales-invoice-report-${new Date().toISOString().split('T')[0]}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting data:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Failed to export sales invoice report',
          },
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Enhanced Sales Invoice Report Styling - Scoped to SalesInvoiceReport component only
.sales-invoice-report-container {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 1.5rem;
}

// Enhanced Header with Gradient - Sales Invoice Report specific (isolated styling)
.sales-invoice-report-container .sales-invoice-report-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
  
  .sales-invoice-header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .sales-invoice-header-text {
      .sales-invoice-report-title {
        color: white;
        font-size: 2.5rem;
        font-weight: 700;
        margin-bottom: 0.5rem;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
      
      .sales-invoice-report-subtitle {
        color: rgba(255, 255, 255, 0.9);
        font-size: 1.1rem;
        margin-bottom: 0;
        font-weight: 300;
      }
    }
    
    .sales-invoice-header-actions {
      .sales-invoice-export-btn {
        background: rgba(255, 255, 255, 0.2);
        border: 2px solid rgba(255, 255, 255, 0.3);
        color: white;
        font-weight: 600;
        padding: 0.75rem 2rem;
        border-radius: 12px;
        transition: all 0.3s ease;
        backdrop-filter: blur(10px);
        
        &:hover {
          background: rgba(255, 255, 255, 0.3);
          border-color: rgba(255, 255, 255, 0.5);
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
        }
        
        &:disabled {
          opacity: 0.6;
          transform: none;
        }
      }
    }
  }
}

// Enhanced Filters Section - Sales Invoice Report specific
.sales-invoice-report-container .sales-invoice-filters-section {
  margin-bottom: 2rem;
  
  .sales-invoice-enhanced-filter-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    border: none;
    overflow: hidden;
    
    .sales-invoice-filter-header {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      padding: 1.5rem;
      
      .sales-invoice-filter-title {
        color: white;
        margin: 0;
        font-weight: 600;
        font-size: 1.3rem;
        display: flex;
        align-items: center;
      }
    }
    
    .filter-content {
      padding: 3rem;
      min-height: 500px;
      
      .enhanced-form-group {
        margin-bottom: 1rem;
        display: flex;
        flex-direction: column;
        height: 100%;
        
        label {
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 0.75rem;
          font-size: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          flex-shrink: 0;
        }
        
        .form-control,
        .vs__dropdown-toggle {
          flex: 1;
          min-height: 55px;
        }
      }
      
      .enhanced-input {
        border: 2px solid #e9ecef;
        border-radius: 10px;
        padding: 1rem 1.25rem;
        font-size: 1rem;
        transition: all 0.3s ease;
        background-color: white;
        height: 55px;
        
        &:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          background-color: white;
        }
      }
      
      .enhanced-select {
        .vs__dropdown-toggle {
          border: 2px solid #e9ecef;
          border-radius: 10px;
          padding: 0.75rem 1rem;
          transition: all 0.3s ease;
          min-height: 55px;
          height: 55px;
          display: flex;
          align-items: center;
          background-color: white;
          
          &:focus-within {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background-color: white;
          }
        }
        
        .vs__selected-options {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.25rem;
        }
        
        .vs__selected {
          background: #667eea;
          color: white;
          border-radius: 6px;
          padding: 0.25rem 0.5rem;
          font-size: 0.85rem;
          margin: 0.125rem;
        }
      }
      
      .filter-actions {
        display: flex;
        justify-content: flex-end;
        gap: 1rem;
        margin-top: 2rem;
        padding-top: 1.5rem;
        border-top: 1px solid #e9ecef;
        
        .action-btn {
          padding: 0.75rem 2rem;
          border-radius: 10px;
          font-weight: 600;
          transition: all 0.3s ease;
          
          &.reset-btn {
            background: #f8f9fa;
            border: 2px solid #dee2e6;
            color: #6c757d;
            
            &:hover {
              background: #e9ecef;
              border-color: #adb5bd;
              transform: translateY(-1px);
            }
          }
          
          &.generate-btn {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: none;
            color: white;
            
            &:hover {
              transform: translateY(-2px);
              box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);
            }
          }
        }
      }
    }
  }
}

// Enhanced Loading State - Sales Invoice Report specific
.sales-invoice-report-container .sales-invoice-loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  
  .loading-content {
    display: flex;
    align-items: center;
    background: white;
    padding: 2rem 3rem;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    
    .loading-text {
      font-size: 1.1rem;
      color: #6c757d;
      font-weight: 500;
    }
  }
}

.pagination-select {
  min-width: 80px;
  background-color: #f8f8f8;
  border-color: #ddd;
}

.pagination-modern {
  .page-item {
    margin: 0 0.2rem;
    
    .page-link {
      border-radius: 6px;
      border: 1px solid #e0e0e0;
      color: #6e6b7b;
      padding: 0.5rem 0.75rem;
      transition: all 0.2s ease;
      
      &:hover {
        background-color: #f8f8f8;
        border-color: #7367f0;
        color: #7367f0;
      }
    }
    
    &.active .page-link {
      background-color: #7367f0;
      border-color: #7367f0;
      color: white;
    }
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
